
import { APIClient } from '../../helpers/api_helper';
import TokenManager from '../../utils/tokenManager';
import secureApi from "../../utils/secureApi";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = new APIClient();

export interface ClubInfo {
  _id: string;
  name: string;
  logo: string;
  emailId: string;
  managerName: string;
  countryCode: number;
  managerContactNum: number;
  location: string;
  isActive: boolean;
  // ✅ NEW FIELDS
  address?: string;
  gstNum?: string;  // API uses gstNum
}


export interface ClubListItem {
  _id: string;
  name: string;
}

interface ClubListApiResponse {
  data?: {
    clubs?: ClubListItem[];
  } | ClubListItem[];
}

let clubsForScopeCache: ClubListItem[] | null = null;
let clubsForScopeInFlight: Promise<ClubListItem[]> | null = null;
export interface UpdateClubPayload {
  name?: string;
  logo?: string;
  emailId?: string;
  managerName?: string;
  countryCode?: number;
  managerContactNum?: number;
  location?: string;
  isActive?: boolean;
  // ✅ NEW FIELDS
  address?: string;
  gstNum?: string;  // API uses gstNum
}

// =========================
// Club Information APIs
// =========================

// Fetch club information by club ID
export const getClubInfo = async (clubId: string): Promise<ClubInfo> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    const response = await api.get(`${API_BASE_URL}/api/v1/club/fetch-club-info/${clubId}`, null);
    
    // Handle the new API response structure
    if (response.data) {
      return response.data as ClubInfo;
    }
    
    return response as unknown as ClubInfo;
  } catch (error) {
    console.error('Failed to fetch club info:', error);
    
    // If it's a token-related error, provide more specific guidance
    if (typeof error === 'string' && error.includes('Token')) {
      console.error('Token-related error detected. Please check authentication.');
      // You might want to redirect to login here
      // window.location.href = '/login';
    }
    
    throw error;
  }
};

// Update club information by club ID
export const updateClubInfo = async (clubId: string, payload: UpdateClubPayload): Promise<ClubInfo> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    const response = await api.create(`${API_BASE_URL}/api/v1/club/update-club-details/${clubId}`, payload);
    
    // Handle the new API response structure
    if (response.data) {
      return response.data as ClubInfo;
    }
    
    return response as unknown as ClubInfo;
  } catch (error) {
    console.error('Failed to update club info:', error);
    
    // If it's a token-related error, provide more specific guidance
    if (typeof error === 'string' && error.includes('Token')) {
      console.error('Token-related error detected. Please check authentication.');
      // You might want to redirect to login here
      // window.location.href = '/login';
    }
    
    throw error;
  }
};

// Get multiple clubs information
export const getMultipleClubInfo = async (clubIds: string[]): Promise<ClubInfo[]> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    const clubInfoPromises = clubIds.map(clubId => getClubInfo(clubId));
    const clubInfoArray = await Promise.all(clubInfoPromises);
    return clubInfoArray;
  } catch (error) {
    console.error('Error fetching multiple club info:', error);
    throw error;
  }
}; 


/**
 * Fetch list of clubs for scope ID selection.
 * Uses /api/v1/club/fetch-clubs and normalizes to a flat array.
 */
export async function listClubsForScope(): Promise<ClubListItem[]> {
  if (clubsForScopeCache) {
    return clubsForScopeCache;
  }

  if (clubsForScopeInFlight) {
    return clubsForScopeInFlight;
  }

  try {
    clubsForScopeInFlight = (async () => {
      const response = await secureApi.get<ClubListApiResponse>(
        "/api/v1/club/fetch-clubs"
      );

      let clubs: ClubListItem[] = [];
      if (Array.isArray(response)) {
        clubs = response as ClubListItem[];
      } else if (Array.isArray(response?.data)) {
        clubs = response.data as ClubListItem[];
      } else if (Array.isArray(response?.data?.clubs)) {
        clubs = response.data.clubs as ClubListItem[];
      }

      clubsForScopeCache = clubs;
      return clubs;
    })();

    return await clubsForScopeInFlight;
  } catch (error) {
    console.error("Failed to fetch clubs for scope IDs:", error);
    return [];
  } finally {
    clubsForScopeInFlight = null;
  }
}